import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Product } from "@/data/products";
import { Button } from "@/components/ui/button";
import { Plus, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { WHATSAPP_NUMBER } from "@/data/products";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

interface ProductDetailProps {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProductDetail({ product, open, onOpenChange }: ProductDetailProps) {
  const { addToCart } = useCart();

  if (!product) return null;

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Olá Gaby! Gostaria de saber mais sobre o produto: ${product.name}`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  const handleAddToCartAndOpenCart = () => {
    addToCart(product);
    onOpenChange(false);
    // Pequeno atraso para garantir que o diálogo de detalhes feche antes de abrir a sacola
    setTimeout(() => {
      const cartTrigger = document.getElementById('cart-trigger');
      if (cartTrigger) {
        cartTrigger.click();
      }
    }, 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-[32px] border-none">
        <div className="relative aspect-square bg-[#FDFCFB]">
          {product.images.length > 1 ? (
            <Carousel className="w-full h-full">
              <CarouselContent>
                {product.images.map((img, index) => (
                  <CarouselItem key={index}>
                    <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2 pointer-events-none">
                <CarouselPrevious className="relative left-0 pointer-events-auto bg-white/80 border-none h-8 w-8" />
                <CarouselNext className="relative right-0 pointer-events-auto bg-white/80 border-none h-8 w-8" />
              </div>
            </Carousel>
          ) : (
            <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
          )}
        </div>
        
        <div className="p-6 space-y-4">
          <DialogHeader>
            <div className="flex justify-between items-start">
              <DialogTitle className="text-xl font-serif font-bold text-gray-900">{product.name}</DialogTitle>
              <span className="text-xl font-bold text-pink-600">
                {new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(product.price)}
              </span>
            </div>
          </DialogHeader>
          
          <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
            {product.description || "Nenhuma descrição disponível."}
          </p>
          
          <div className="flex gap-3 pt-2 pb-2">
            <Button 
              onClick={handleAddToCartAndOpenCart}
              className="flex-1 h-12 rounded-2xl bg-pink-500 hover:bg-pink-600 text-white font-semibold"
            >
              <Plus className="mr-2 h-4 w-4" />
              Fazer Encomenda
            </Button>
            <Button 
              variant="outline"
              onClick={handleWhatsApp}
              className="h-12 w-12 rounded-2xl border-pink-100 text-pink-500 hover:bg-pink-50 hover:text-pink-600"
            >
              <MessageCircle className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
